import {
  Box,
  Card,
  CardHeader,
  ImageList,
  ImageListItem,
} from "@mui/material";
import React from "react";
import { useRecordContext } from "react-admin";
import ImagePreviewerDialog from "./ImagePreviewerDialog";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

interface MediaGridProps {
  items: string[];
  label: string;
  onClick: (images: string[], index: number) => void;
}

const checkInSections = [
  { label: "CheckIn - Selfies", path: "checkInImages.selfiesImages" },
  { label: "CheckIn - Kitchen", path: "checkInImages.kitchenImages" },
];

const checkOutSections = [
  { label: "CheckOut - Food", path: "checkOutImages.foodImages" },
  { label: "CheckOut - Kitchen", path: "checkOutImages.kitchenImages" },
];

const getValueByPath = (obj: any, path: string) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

const MediaGrid: React.FC<MediaGridProps> = ({ items = [], label, onClick }) => {
  if (!items.length)
    return (
      <Card sx={{ mb: 1 }}>
        <CardHeader
          title={label}
          sx={{
            py: 1,
            "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 600 },
          }}
        />
        <Box sx={{ px: 1.5, pb: 1, color: "gray", fontSize: 12 }}>
          No media uploaded.
        </Box>
      </Card>
    );

  return (
    <Card sx={{ mb: 1 }}>
      <CardHeader
        title={label}
        sx={{
          py: 1,
          "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 600 },
        }}
      />

      <ImageList cols={4} gap={3} sx={{ px: 1, pb: 1 }}>
        {items.map((url, i) => {
          const isVideo = /\.(mp4|webm|ogg)$/i.test(url);
          return (
            <ImageListItem
              key={i}
              onClick={() => onClick(items, i)}
              sx={{
                cursor: "pointer",
                height: 80,          // ⬅ smaller
                width: "100%",
                borderRadius: 1,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {isVideo ? (
                <>
                  <video
                    src={url}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <PlayCircleOutlineIcon
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: 22,
                      color: "white",
                      opacity: 0.9,
                    }}
                  />
                </>
              ) : (
                <img
                  src={url}
                  loading="lazy"
                  alt="media"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </ImageListItem>
          );
        })}
      </ImageList>
    </Card>
  );
};
const CheckInAndOutImages = () => {
  const record = useRecordContext();
  const [open, setOpen] = React.useState(false);
  const [previewImages, setPreviewImages] = React.useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = React.useState(0);

  const openMedia = (images: string[], index: number) => {
    setPreviewImages(images);
    setPreviewIndex(index);
    setOpen(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        width: "100%",
      }}
    >
      {/* LEFT COLUMN — CHECK-IN */}
      <Box
        sx={{
          flex: 1,
          maxHeight: "75vh",
          overflowY: "auto",
          pr: 1,
        }}
      >
        {checkInSections.map(({ label, path }) => (
          <MediaGrid
            key={path}
            label={label}
            items={getValueByPath(record, path)}
            onClick={openMedia}
          />
        ))}
      </Box>

      {/* RIGHT COLUMN — CHECK-OUT */}
      <Box
        sx={{
          flex: 1,
          maxHeight: "75vh",
          overflowY: "auto",
          pl: 1,
        }}
      >
        {checkOutSections.map(({ label, path }) => (
          <MediaGrid
            key={path}
            label={label}
            items={getValueByPath(record, path)}
            onClick={openMedia}
          />
        ))}
      </Box>

      {open && (
        <ImagePreviewerDialog
          src={previewImages[previewIndex] ?? ""}
          images={previewImages}
          initialIndex={previewIndex}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default CheckInAndOutImages;
